import {useScrollTrigger, Zoom, Box, Typography} from '@mui/material';
import {BoxProps} from '@jobseeker/lotus/foundations';
import BottomSheet from '@jobseeker/lotus/elements/BottomSheet';
import type {Theme} from '@mui/material';
import Alert from '@jobseeker/lotus/components/Alert';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  WebBrowserLogo,
  IimjobsLogo,
  IimjobsLogoOutlined,
  CrossOutlined,
  Prem,
} from '@jobseeker/lotus/assets/icons';
import {
  getAppDownloadLinkagesUrls,
  openUrl,
} from '@jobseeker/common-core/utils/common';
import {Dispatch, SetStateAction, useEffect, useRef, useState} from 'react';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {
  getUbaPageName,
  handleTracking,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {shallowEqual, useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import {HiristAppIconNew} from '@jobseeker/lotus/assets/images/resources/new-logo';
import {
  getItemWithExpiry,
  setItemWithExpiry,
} from '@jobseeker/feature-ads/utils';
type PromotionProps = {
  theme: Theme;
  style?: BoxProps;
  setPromotionType: Dispatch<SetStateAction<string>>;
  showbottom?: boolean;
  showFreeProBannerToday?: boolean;
  isCohortEligible?: boolean;
};

type BannerType = {
  promotionType: React.FC<any>;
  type: string;
};

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const handleUbaTracking = (
  action = 'view',
  linkageName = 'Listing Page Bottom Sheet Banner',
  typeClick = 'CTA'
) => {
  const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME as 'iimjobs' | 'hirist';
  if (
    !linkageName.includes('Similar Jobs Page Bottom Sheet Banner') &&
    PLATFORM === 'iimjobs'
  ) {
    return;
  }

  const trackingData = {
    pageName: getUbaPageName(),
    linkageName: linkageName,
    platform: PLATFORM,
    linkageType: 'bottomsheetBanner',
    sessionUsername: sessionStorage.getItem('userId') || '',
    ...(action === 'click' && {typeClick: typeClick}),
  };
  TrackEventUBA(
    action === 'click' ? 'appDownloadLinkageClick' : 'appDownloadLinkageView',
    action,
    trackingData
  );
};

const AppPromoBottomSheet: React.FC<{
  onContinueWithApp: () => void;
  onGetApp: () => void;
  handleCTAClick: (type?: string) => void;
  isAppIimjobs: boolean;
  showFreeProBannerToday?: boolean;
}> = ({
  onContinueWithApp,
  onGetApp,
  handleCTAClick,
  isAppIimjobs,
  showFreeProBannerToday,
}) => (
  <Box
    display="flex"
    flexDirection="column"
    mx={isAppIimjobs ? 0 : 3.5}
    my={isAppIimjobs ? 0 : 3}
  >
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      backgroundColor={showFreeProBannerToday ? '#EFFFF8' : ''}
      mb={isAppIimjobs ? 0 : 2.5}
      pb={isAppIimjobs ? 2.3 : 0}
      px={isAppIimjobs ? 3.25 : 0}
      pt={isAppIimjobs ? 2 : 0}
    >
      <Box display="flex" justifyContent="center" alignItems="center">
        <Box
          width={50}
          height={50}
          display="flex"
          justifyContent="center"
          alignItems="center"
          mr={1.5}
        >
          <Image
            src={isAppIimjobs ? IimjobsLogoOutlined : HiristAppIconNew}
            alt={isAppIimjobs ? 'iimjobsLogoOutlined' : 'hiristLogo'}
            title={isAppIimjobs ? 'iimjobsLogoOutlined' : 'hiristLogo'}
            height={50}
            width={50}
          />
        </Box>
        <Box>
          <Typography
            variant={showFreeProBannerToday ? 'subtitle2' : 'subtitle1'}
          >
            {isAppIimjobs ? 'iimjobs app' : 'Hirist app'}
          </Typography>
          {showFreeProBannerToday && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{whiteSpace: 'nowrap'}}
            >
              <Image
                src={Prem}
                alt="Premium"
                title="Premium"
                height={12}
                width={12}
              />
              <Typography
                ml={0.5}
                mt={0.25}
                fontSize={10}
                fontWeight={400}
                noWrap
              >
                Pro membership - FREE
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Button
        sx={{
          fontSize: '13px',
          background: '#000',
          px: 2,
          py: 0.75,
          width: 90,
          height: 'fit-content',
          backgroundColor: isAppIimjobs ? 'secondary.main' : 'primary.main',
        }}
        variant="contained"
        size="small"
        onClick={() => {
          setTimeout(() => {
            handleCTAClick('Bottom_sheet_banner');
            handleUbaTracking(
              'click',
              showFreeProBannerToday
                ? 'Similar Jobs Page Bottom Sheet Banner - PRO'
                : 'Similar Jobs Page Bottom Sheet Banner',
              'CTA'
            );
            onGetApp();
          }, 0);
        }}
        data-testid="open_in_mobile_app"
      >
        Open
      </Button>
    </Box>
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mt={isAppIimjobs ? 2.3 : 0}
      px={isAppIimjobs ? 3.25 : 0}
      pb={isAppIimjobs ? 2 : 0}
    >
      <Box display="flex" justifyContent="center" alignItems="center">
        <Box
          width={49}
          height={49}
          sx={{border: '1px solid #E4EEFC', borderRadius: 2}}
          display="flex"
          justifyContent="center"
          alignItems="center"
          mr={1.5}
        >
          <Image
            src={WebBrowserLogo}
            alt="Web browser"
            title="Web browser"
            height={30}
            width={30}
          />
        </Box>
        <Typography
          variant={showFreeProBannerToday ? 'subtitle2' : 'subtitle1'}
        >
          Web browser
        </Typography>
      </Box>
      <Button
        sx={{
          fontSize: '13px',
          background: '#000',
          px: 2,
          py: 0.75,
          width: 90,
          height: 'fit-content',
          color: 'primary.main',
          border: '1px solid',
          borderColor: 'primary.main',
          backgroundColor: 'transparent',
        }}
        variant="contained"
        size="small"
        onClick={() => {
          setTimeout(() => {
            handleCTAClick('Continue_with_browser_btn');
            handleUbaTracking(
              'click',
              showFreeProBannerToday
                ? 'Similar Jobs Page Bottom Sheet Banner - PRO'
                : 'Similar Jobs Page Bottom Sheet Banner',
              'continueWithBrowser'
            );
            onContinueWithApp();
          }, 0);
        }}
        data-testid="continue_with_web_app"
      >
        Continue
      </Button>
    </Box>
  </Box>
);

const Promotions = ({
  theme,
  setPromotionType,
  style = {
    position: 'fixed',
    bottom: theme.spacing(2),
  },
  showbottom,
  showFreeProBannerToday,
  isCohortEligible,
}: PromotionProps) => {
  const metadata: any = useInterceptor();
  const {downloadLinks} = metadata;

  const [showAppPromoBanner, setShowAppPromoBanner] = useState(false);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [openDrop, setDropOpen] = useState(true);
  const [triggerState, setTriggerState] = useState(false);
  const [banner, setBanner] = useState<BannerType | null>(null);
  const page = useSelector((state: AppState) => state.app.page, shallowEqual);
  const isLoggedIn = useSelector((state: AppState) => state.auth.isLoggedIn);
  const [bannerPromoDataLoaded, setBannerPromoDataLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const [showBannerGetApp, setShowBannerGetApp] = useState(false);

  const sendEvent = (
    eventCategory: string,
    eventName: string,
    action: string,
    bannerType?: string
  ) => {
    const trackingData: Record<string, string> = {
      event_category: eventCategory,
      Origin: page,
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
    };

    if (bannerType) {
      trackingData.Banner_Type = bannerType;
    }

    handleTracking(`${eventName}_${action}`, trackingData);
  };

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 700,
  });
  useEffect(() => {
    if (trigger) {
      setTriggerState(true);
    }
  }, [trigger]);

  const calculateRandomizeLogic = () => {
    if (showAppPromoBanner) {
      const getRandomBanner = () => {
        const banners: BannerType[] = [
          // {promotionType: FloatingCtaComponent, type: 'Floating CTA'},
          // {promotionType: GetAppComponent, type: 'Get App'},
          {promotionType: InstallNowComponent, type: 'Install Now'},
        ];
        const randomIndex = Math.floor(Math.random() * banners.length);
        return banners[randomIndex];
      };
      const selectedBanner = getRandomBanner();
      setBanner(selectedBanner);
      if (page !== 'jobapplied') {
        setPromotionType(selectedBanner.type);
      }
    }
    if (showBottomSheet && page === 'jobapplied') {
      handleUbaTracking(
        'view',
        showFreeProBannerToday
          ? 'Similar Jobs Page Bottom Sheet Banner - PRO'
          : 'Similar Jobs Page Bottom Sheet Banner'
      );
      sendEvent('App_promotion', 'Bottom_sheet_banner', 'View', '');
    }
  };
  const triggerBannerShowLogic = async () => {
    calculateRandomizeLogic();
  };

  useEffect(() => {
    if (!bannerPromoDataLoaded) {
      triggerBannerShowLogic();
    } else {
      calculateRandomizeLogic();
    }
  }, [showAppPromoBanner, bannerPromoDataLoaded]);

  useEffect(() => {
    if (showAppPromoBanner && !bannerPromoDataLoaded) {
      if (triggerState && banner && page !== 'jobapplied') {
        sendEvent('App_promotion', 'Regular_banner', 'View', banner.type);
      }
      if (triggerState && banner && banner.type == 'Install Now') {
        handleUbaTracking('view', 'Listing Page Bottom Sheet Banner');
      }
    }
  }, [showAppPromoBanner, triggerState, bannerPromoDataLoaded, banner]);

  const handleButtonClick = () => {
    let storeUrl = '';
    if (showBottomSheet && page === 'jobapplied') {
      storeUrl = isAppIimjobs
        ? downloadLinks.bottomSheet.url
        : getAppDownloadLinkagesUrls(4);
      openUrl(storeUrl, '_blank');
      handleDropClose();
      return;
    }
    storeUrl =
      banner?.type === 'Floating CTA'
        ? downloadLinks.floatingCta.url
        : banner?.type === 'Get App'
        ? downloadLinks.getApp.url
        : isAppIimjobs
        ? downloadLinks.installNow.url
        : getAppDownloadLinkagesUrls(3);
    openUrl(storeUrl, '_blank');
    if (showAppPromoBanner && page !== 'jobapplied') {
      handleBannerClose();
    }
  };

  const handleBannerClose = () => {
    const ttl = 24 * 60 * 60 * 1000;
    setItemWithExpiry('HIDE_APP_PROMO_BANNER', '1', ttl);
    setShowAppPromoBanner(false);
  };

  useEffect(() => {
    const hideBanner = getItemWithExpiry('HIDE_APP_PROMO_BANNER');
    if (!hideBanner && showbottom) {
      setShowAppPromoBanner(true);
    }
    const hideBottomSheet = getItemWithExpiry('HIDE_BOTTOM_SHEET_BANNER');
    if (!hideBottomSheet && page === 'jobapplied' && showbottom) {
      setShowBottomSheet(true);
    }
  }, []);

  const handleDropClose = () => {
    setDropOpen(false);
    const ttl = 24 * 60 * 60 * 1000;
    setItemWithExpiry('HIDE_BOTTOM_SHEET_BANNER', '1', ttl);
    setShowBottomSheet(false);
  };

  const FloatingCtaComponent: React.FC<{
    handleCTAClick?: () => void;
    trigger?: boolean;
  }> = ({handleCTAClick, trigger}) => {
    const ctaButton = (
      <Box
        sx={{
          left: '38%',
          position: 'fixed',
          width: '100%',
          bottom: '25px',
          zIndex: 12,
        }}
      >
        <Button
          sx={{
            fontSize: '13px',
            width: {md: '125px'},
            background: '#000',
            mt: {xxs: 1, md: 2},
            px: 2,
            py: 0.75,
            height: 'fit-content',
            backgroundColor: 'secondary.main',
            '&:hover': {
              backgroundColor: 'secondary.main',
            },
          }}
          variant="contained"
          size="small"
          onClick={handleCTAClick}
          data-testid="floating_cta"
        >
          Get App
        </Button>
      </Box>
    );

    return <Zoom in={trigger}>{ctaButton}</Zoom>;
  };

  const GetAppComponent: React.FC<{
    handleCTAClick: () => void;
    handleCrossClick: () => void;
    trigger: boolean;
  }> = ({handleCTAClick, handleCrossClick, trigger}) => (
    <Zoom in={trigger}>
      <Box sx={style}>
        <Alert
          width={'100%'}
          background={`url(${process.env.NEXT_PUBLIC_STATIC_IIMJOBS}/static/images/BannerBackground.svg)`}
          icon={
            <Box height={50} width={50} my={0.375}>
              <Image
                src={IimjobsLogo}
                alt="iimjobsLogo"
                title="Scroll Up"
                height={50}
                width={50}
              />
            </Box>
          }
          variant={VersionVariants.VERSION_TWO}
          show={true}
          onClose={handleCrossClick}
          whiteCross={true}
        >
          <Box
            display="flex"
            flexDirection={'column'}
            alignItems={'flex-start'}
          >
            <Box
              width="100%"
              display="flex"
              flexDirection="column"
              justifyContent="center"
            >
              <Typography variant="subtitle2" color="#ffffff">
                Stay updated, always!
              </Typography>
              <Typography
                variant="subtitle3"
                color="#ffffff"
                sx={{wordWrap: 'break-word'}}
              >
                Access instant job notifications through our app!
              </Typography>
            </Box>
            <Button
              sx={{
                fontSize: '13px',
                background: '#000',
                px: 2,
                py: 0.75,
                height: 'fit-content',
                backgroundColor: 'secondary.main',
                marginTop: '8px',
              }}
              variant="contained"
              size="small"
              onClick={handleCTAClick}
              data-testid="get_app"
            >
              Get App
            </Button>
          </Box>
        </Alert>
      </Box>
    </Zoom>
  );

  // Logic to handle onClose triggering twice — once when the user clicks the X, and again during the animation teardown.
  const hasTrackedCloseRef = useRef(false);
  const handleClose = () => {
    if (!hasTrackedCloseRef.current) {
      hasTrackedCloseRef.current = true;
      handleUbaTracking('click', 'Listing Page Bottom Sheet Banner', 'Close');
    }
  };

  const InstallNowComponent: React.FC<{
    handleCTAClick: () => void;
    handleCrossClick: () => void;
    trigger: boolean;
  }> = ({handleCTAClick, handleCrossClick, trigger}) => {
    TrackEventUBA('appDownloadLinkageView', 'View', {
      platform: process.env.NEXT_PUBLIC_APP_NAME,
      linkageName: 'Install App On Listing pages',
      linkageType: 'BottomSheet',
      pageName: getUbaPageName(),
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
    return (
      <Zoom in={trigger}>
        <Box display="flex" sx={style}>
          <Alert
            width={'100%'}
            background={`url(${process.env.NEXT_PUBLIC_STATIC_IIMJOBS}/static/images/BannerBackground.svg) no-repeat center/cover`}
            icon={
              <Box height={50} width={50} my={0.375}>
                <Image
                  src={isAppIimjobs ? IimjobsLogo : HiristAppIconNew}
                  alt={isAppIimjobs ? 'iimjobsLogo' : 'hiristLogo'}
                  title={isAppIimjobs ? 'iimjobsLogo' : 'hiristLogo'}
                  height={50}
                  width={50}
                  style={{
                    borderRadius: 15,
                  }}
                />
              </Box>
            }
            variant={VersionVariants.VERSION_TWO}
            show={true}
            onClose={() => {
              setTimeout(() => {
                handleClose();
                handleCrossClick();
              }, 0);
            }}
            whiteCross={true}
          >
            <Box
              display="flex"
              flexDirection={'column'}
              alignItems={'flex-start'}
            >
              <Box
                width="100%"
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <Typography
                  variant={isAppIimjobs ? 'subtitle2' : 'subtitle1'}
                  fontWeight={isAppIimjobs ? 500 : 700}
                  color="#ffffff"
                >
                  {isAppIimjobs
                    ? 'Download the iimjobs mobile app!'
                    : 'Get instant job alerts with the Hirist app'}
                </Typography>
              </Box>
              <Button
                sx={{
                  fontSize: '13px',
                  color: isAppIimjobs ? '#fff' : '#000',
                  background: isAppIimjobs ? 'secondary.main' : '#fff',
                  px: 2,
                  py: isAppIimjobs ? 0.75 : 0.5,
                  height: 'fit-content',
                  backgroundColor: isAppIimjobs ? 'secondary.main' : '#fff',
                  marginTop: isAppIimjobs ? '8px' : '6px',
                }}
                variant="contained"
                size="small"
                onClick={() => {
                  setTimeout(() => {
                    handleUbaTracking(
                      'click',
                      'Listing Page Bottom Sheet Banner',
                      'CTA'
                    );
                    handleCTAClick();
                  }, 0);
                }}
                data-testid="install_now"
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: isAppIimjobs ? '#fff' : 'primary.main',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}
                >
                  {!isAppIimjobs && (
                    <Image
                      src={`${process.env.NEXT_PUBLIC_STATIC_IIMJOBS}/static/images/download-hirist.svg`}
                      alt=""
                      title={'hirist download logo'}
                      height={13}
                      width={13}
                      style={{marginRight: 4}}
                    />
                  )}
                  Install Now
                </Box>
              </Button>
            </Box>
          </Alert>
        </Box>
      </Zoom>
    );
  };

  const BottomSheetComponent: React.FC<{
    trigger: boolean;
    openDrop: boolean;
    handleDropClose: () => void;
    handleCTAClick: () => void;
  }> = ({trigger, openDrop, handleDropClose, handleCTAClick}) => {
    TrackEventUBA('appDownloadLinkageView', 'View', {
      platform: process.env.NEXT_PUBLIC_APP_NAME,
      linkageName: showFreeProBannerToday
        ? 'Similar Jobs Page Bottom Sheet Banner - PRO'
        : 'Similar Jobs Page Bottom Sheet Banner',
      linkageType: 'BottomSheet',
      pageName: getUbaPageName(),
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
    return (
      <>
        <Zoom in={trigger}>
          <Box sx={style}>
            <BottomSheet
              maxHeight={230}
              borderRadius="16px 16px 0 0"
              header={
                <Box p={2}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      cursor: 'default',
                      alignItems: 'center',
                      paddingLeft: 1,
                      paddingRight: 0,
                    }}
                  >
                    <Typography variant="subtitle1">
                      Continue applying to jobs with
                    </Typography>
                    <Image
                      src={CrossOutlined}
                      alt={'Close Icon'}
                      width={18}
                      height={18}
                      onClick={() => {
                        setTimeout(() => {
                          handleUbaTracking(
                            'click',
                            showFreeProBannerToday
                              ? 'Similar Jobs Page Bottom Sheet Banner - PRO'
                              : 'Similar Jobs Page Bottom Sheet Banner',
                            'Close'
                          );
                          handleDropClose();
                        }, 0);
                      }}
                      objectFit="cover"
                      style={{cursor: 'pointer'}}
                    />
                  </Box>
                </Box>
              }
              onDismiss={() => {
                setTimeout(() => {
                  handleDropClose();
                }, 0);
              }}
              open={openDrop}
              bannerMode={false}
              snapPoints={() => [isAppIimjobs ? 262 : 235]}
            >
              <AppPromoBottomSheet
                onContinueWithApp={handleDropClose}
                onGetApp={handleButtonClick}
                handleCTAClick={handleCTAClick}
                isAppIimjobs={isAppIimjobs}
                showFreeProBannerToday={showFreeProBannerToday}
              />
            </BottomSheet>
          </Box>
        </Zoom>
      </>
    );
  };

  if (page !== 'jobapplied' && !showAppPromoBanner) {
    setPromotionType('');
    return null;
  }
  if (page === 'jobapplied' && !showBottomSheet) {
    setPromotionType('');
    return null;
  }

  const ComponentToShow = banner?.promotionType;

  return (
    <>
      {loading ? (
        <></>
      ) : (
        <>
          {' '}
          {page === `jobapplied` && showbottom ? (
            isCohortEligible && !showFreeProBannerToday ? (
              <></>
            ) : (
              <BottomSheetComponent
                trigger={trigger}
                openDrop={openDrop}
                handleDropClose={handleDropClose}
                handleCTAClick={(type?: string) => {
                  sendEvent('App_promotion', type || '', 'Click', '');
                }}
              />
            )
          ) : (
            ComponentToShow &&
            showbottom && (
              <ComponentToShow
                handleCTAClick={() => {
                  sendEvent(
                    'App_promotion',
                    'Regular_banner',
                    'Click',
                    banner.type
                  );
                  handleButtonClick();
                }}
                handleCrossClick={handleBannerClose}
                trigger={trigger}
              />
            )
          )}
        </>
      )}
    </>
  );
};

export default Promotions;
